import React, { useEffect, useState } from "react";
import Button from "../../form/front/Button";
import { useSelector } from "react-redux";
import { saveJob } from "../../../services/postService";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import AboutCompany from "./AboutCompany";

const JobDetailsCard = ({ jobDetails, onApplyClick }) => {
  if (!jobDetails?.data) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-24 h-24 mb-6 rounded-full bg-slate-100 dark:bg-slate-200/20 flex items-center justify-center">
          <i className="fa-regular fa-briefcase text-4xl text-slate-300 dark:text-gray-100"></i>
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-800 dark:text-gray-200 mb-2 text-center">
          No Job Selected
        </h3>
        <p className="text-sm md:text-base text-slate-500 dark:text-gray-300 text-center max-w-md">
          Select a job from the list to view detailed information, requirements,
          and apply.
        </p>
      </div>
    );
  }

  const { data } = jobDetails;
  const formatLabel = (text) =>
    text
      ?.split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  return (
    <>
      {/* Job Header */}
      <JobHeader
        title={data.content?.en?.title}
        companyName={data.organization?.content?.en?.title}
        companyDescription={data.organization?.content?.en?.content}
        companyLogo={data.organization?.logo?.url}
      />

      {/* Job Info */}
      <JobInfo
        jobType={data.job?.jobType}
        experience={data.job?.experience?.content?.en?.title}
        employeeCount={data.organization?.businessInfo?.employeeCount}
        skills={data.job?.skills}
        location={data.location}
        locationType={data.locationType}
        salary={data.job?.budget}
      />

      <div className="flex items-center space-x-2 flex-wrap">
        {[data.locationType, data.job?.jobType]
          .filter(Boolean)
          .map((item, index) => (
            <div
              key={index}
              className="border border-gray-400 rounded-full h-8 flex items-center"
            >
              <div className="text-gray-600 text-sm font-semibold px-4">
                {formatLabel(item)}
              </div>
            </div>
          ))}
      </div>

      {/* Action Buttons */}
      <JobActions
        onApplyClick={onApplyClick}
        jobId={data?._id}
        isSavedFromApi={data?.saved}
        hasApplied={data?.applyJob}
      />

      {/* Job Description */}
      <JobDescription
        content={data.content?.en?.content}
        organization={data.organization}
      />
    </>
  );
};

// Job Header Component
const JobHeader = ({ title, companyName, companyDescription }) => {
  return (
    <div className="flex-grow space-y-1">
      <h2 className="text-lg md:text-xl text-black dark:text-gray-100 capitalize font-bold leading-tight">
        {title || "Untitled Job"}
      </h2>
      {/* <div className="text-sm md:text-sm text-slate-600 dark:text-gray-200 font-semibold">
        {companyName || "Unknown Company"}
      </div>
      {companyDescription && (
        <p className="text-xs md:text-sm text-slate-500 dark:text-gray-300 line-clamp-2">
          {companyDescription}
        </p>
      )} */}
    </div>
  );
};

// Job Info Component
const JobInfo = ({
  jobType,
  experience,
  employeeCount,
  skills,
  location,
  locationType,
  salary,
}) => {
  const formatText = (text) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  const formatSalary = (salary) => {
    if (!salary?.min || !salary?.max) return null;

    const unitSymbol = salary.unit === "usd" ? "$" : "₹";

    const formatValue = (val) => (val >= 1000 ? `${val / 1000}k` : val);

    return `${unitSymbol}${formatValue(
      salary.min,
    )} - ${unitSymbol}${formatValue(salary.max)}`;
  };
  const salaryText = formatSalary(salary);

  const infoItems = [
    {
      icon: "fa-solid fa-briefcase",
      text:
        [jobType, experience].filter(Boolean).map(formatText).join(" • ") ||
        "N/A",
      show: jobType || experience,
    },
    {
      icon: "fa-solid fa-building",
      text: employeeCount
        ? `${employeeCount} employees`
        : "Employee count not specified",
      show: true,
    },
    {
      icon: "fa-solid fa-money-bill-wave",
      text: salaryText || "Salary not disclosed",
      show: !!salaryText,
    },
    {
      icon: "fa-solid fa-code",
      text:
        skills
          ?.map((s) => s.content?.en?.title)
          .filter(Boolean)
          .join(", ") || "Skills not specified",
      show: skills && skills.length > 0,
    },
  ];

  return (
    <div className="space-y-3 py-5">
      {infoItems.map(
        (item, index) =>
          item.show && (
            <div key={index} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-palatte/10 dark:bg-purple-100 flex items-center justify-center">
                <i className={`${item.icon} text-palatte text-sm`}></i>
              </div>
              <div className="flex-grow text-slate-600 dark:text-gray-300 font-medium text-sm md:text-sm pt-1">
                {item.text}
              </div>
            </div>
          ),
      )}
    </div>
  );
};

const JobActions = ({ onApplyClick, jobId, isSavedFromApi, hasApplied }) => {
  const user = useSelector((state) => state?.profile?.data);
  const [isSaved, setIsSaved] = useState(!!isSavedFromApi);
  const [applied, setApplied] = useState(!!hasApplied);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    setApplied(!!hasApplied);
  }, [hasApplied]);

  useEffect(() => {
    setIsSaved(!!isSavedFromApi);
  }, [isSavedFromApi]);

  const handleApplyClick = async () => {
    if (!user) {
      navigate("/signin", {
        state: { from: location.pathname },
      });
      return;
    }
    if (applied) return;

    try {
      await onApplyClick?.(); // assuming this calls API
      //setApplied(true); // 🔥 immediately update UI
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveJob = async () => {
    if (!user) {
      navigate("/signin", {
        state: { from: location.pathname },
      });
      return;
    }
    if (!jobId) {
      toast.error("Refresh and try again");
      return;
    }
    setLoading(true);
    try {
      await saveJob({
        referenceId: jobId,
        reactionType: "save",
      });
      setIsSaved((prev) => !prev); // toggle saved state
      toast.success(
        isSaved ? "Job removed from saved" : "Job saved successfully",
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleShareJob = async () => {
    if (!jobId) {
      toast.error("Unable to share this job");
      return;
    }

    const shareUrl = `${window.location.origin}/jobs/${jobId}`;

    try {
      // If browser supports native share (mobile mostly)
      if (navigator.share) {
        await navigator.share({
          title: "Check out this job",
          text: "I found this job opportunity for you.",
          url: shareUrl,
        });
      } else {
        // Fallback → copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Job link copied to clipboard");
      }
    } catch (err) {
      console.error(err);
      toast.error("Unable to share job");
    }
  };
  return (
    <div className="flex items-center gap-2 py-5 border-b border-slate-200 dark:border-gray-500 flex-wrap">
      <Button
        buttonLabel={applied ? "Applied" : "Apply Now"}
        buttonIconPosition="right"
        buttonIcon="fa-solid fa-arrow-up-right-from-square"
        buttonLabelClasses="text-sm text-white capitalize group-hover:text-palatte font-semibold"
        buttonClasses="bg-palatte h-9 rounded-full px-4 hover:bg-transparent border border-palatte group hover:text-palatte transition-all duration-200 shadow-sm hover:shadow"
        buttonFunction={handleApplyClick}
        isDisable={applied}
      />
      <Button
        buttonLabel={isSaved ? "Saved" : "Save Job"}
        buttonIconPosition="right"
        buttonIcon="fa-regular fa-bookmark"
        buttonLabelClasses="text-sm text-white capitalize group-hover:!text-palatte4 font-semibold"
        buttonClasses="bg-palatte4 h-9 rounded-full px-4 hover:bg-transparent border border-palatte4 group hover:text-palatte4 transition-all duration-200 shadow-sm hover:shadow"
        buttonFunction={handleSaveJob}
        isDisable={isSaved || loading}
      />
      <Button
        // buttonLabel="Share"
        buttonIconPosition="right"
        buttonIcon="fa-solid fa-share"
        buttonLabelClasses="text-base text-white capitalize group-hover:text-palatte6 font-semibold"
        buttonClasses="bg-transparent border-0 h-9 rounded-lg px-6 hover:bg-transparent group hover:text-gray-600 transition-all duration-200 shadow-sm absolute top-5 right-0 text-gray-500 shadow-none !text-base"
        buttonFunction={handleShareJob}
      />
    </div>
  );
};

const JobDescription = ({ content, organization }) => {
  return (
    <div className="py-5">
      {content ? (
        <div
          className=" text-slate-600 dark:text-gray-200 text-sm"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      ) : (
        <p className="text-slate-500 dark:text-gray-300 text-sm md:text-base italic">
          No job description available.
        </p>
      )}
      <div className="mt-5 px-4 md:px-5 py-4 md:py-5 border border-slate-200 dark:border-gray-500 dark:bg-white/10 rounded-md md:rounded-3xl overflow-hidden relative">
        <AboutCompany organization={organization} />
      </div>
    </div>
  );
};

export default JobDetailsCard;
